// =============================================================
//  mayaLinks — Parsing des liens [DOC:type:id|Titre] générés
//  par Claude + récupération du document complet depuis Firestore
//  pour permettre la navigation vers DocumentDetailsScreenUser.
// =============================================================

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../private/firebaseConfig';

// Format toléré pour les liens documents : [DOC:type:id|Titre affiché]
const DOC_TOKEN_REGEX = /\[DOC:([a-zA-Z0-9_]+):([A-Za-z0-9_-]+)\|([^\]]+)\]/g;

// Markdown inline : **gras**, __souligné__, *italique*
// On évite *italique* qui prend mal car le ** est aussi composé de *.
// L'ordre des alternatives compte : ** avant *.
const MD_INLINE_REGEX = /(\*\*([^*\n]+?)\*\*|__([^_\n]+?)__|\*([^*\n]+?)\*)/g;

/**
 * Parse le markdown inline d'un texte brut.
 * Renvoie une liste de segments { type:'text', text } ou
 * { type:'style', styleKind:'bold'|'italic'|'underline', text }.
 */
const parseInlineMarkdown = (text) => {
  const segments = [];
  if (!text) return segments;
  let lastIndex = 0;
  let match;
  MD_INLINE_REGEX.lastIndex = 0;
  while ((match = MD_INLINE_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', text: text.slice(lastIndex, match.index) });
    }
    let styleKind;
    let inner;
    if (match[2] !== undefined) {
      styleKind = 'bold';
      inner = match[2];
    } else if (match[3] !== undefined) {
      styleKind = 'underline';
      inner = match[3];
    } else {
      styleKind = 'italic';
      inner = match[4];
    }
    segments.push({ type: 'style', styleKind, text: inner });
    lastIndex = MD_INLINE_REGEX.lastIndex;
  }
  if (lastIndex < text.length) {
    segments.push({ type: 'text', text: text.slice(lastIndex) });
  }
  return segments;
};

/**
 * Découpe un texte en segments :
 *   { type:'text', text }
 *   { type:'link', docType, id, label }
 *   { type:'style', styleKind:'bold'|'italic'|'underline', text }
 * Les liens [DOC:...] sont extraits en premier, puis on parse
 * le markdown inline (**, __, *) sur le texte restant.
 */
export const parseMayaText = (text) => {
  if (!text || typeof text !== 'string') return [{ type: 'text', text: '' }];

  const segments = [];
  let lastIndex = 0;
  let match;
  DOC_TOKEN_REGEX.lastIndex = 0;
  while ((match = DOC_TOKEN_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      // Parse le markdown dans le texte avant le lien
      const pre = text.slice(lastIndex, match.index);
      segments.push(...parseInlineMarkdown(pre));
    }
    segments.push({
      type: 'link',
      docType: match[1],
      id: match[2],
      label: match[3].trim(),
    });
    lastIndex = DOC_TOKEN_REGEX.lastIndex;
  }
  if (lastIndex < text.length) {
    segments.push(...parseInlineMarkdown(text.slice(lastIndex)));
  }
  if (segments.length === 0) {
    segments.push({ type: 'text', text });
  }
  return segments;
};

// Petit cache mémoire pour éviter de relire Firestore en boucle si l'utilisateur reclique
const docCache = new Map();

/**
 * Récupère le document complet depuis Firestore pour permettre la navigation.
 * @param {string} docType - 'concours' | 'bords' | 'documents'
 * @param {string} id
 * @returns {Promise<object|null>} le document (avec id et type) ou null si introuvable
 */
export const fetchMayaDoc = async (docType, id) => {
  const key = `${docType}/${id}`;
  if (docCache.has(key)) return docCache.get(key);
  try {
    const snap = await getDoc(doc(db, docType, id));
    if (!snap.exists()) {
      docCache.set(key, null);
      return null;
    }
    const data = { id: snap.id, type: docType, ...snap.data() };
    docCache.set(key, data);
    return data;
  } catch (e) {
    if (__DEV__) console.warn('[Maya] fetchMayaDoc failed', e?.message || e);
    return null;
  }
};
